import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuCheck } from "react-icons/lu";
import { api, ApiError , errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { User } from "../lib/types";
import { Avatar } from "../components/Avatar";
import { Button, ErrorBanner, Input, Label } from "../components/ui";

const PRESET_COLOURS = [
  "#6366f1", // indigo
  "#10b981", // emerald
  "#f43f5e", // rose
  "#f59e0b", // amber
  "#0ea5e9", // sky
  "#8b5cf6", // violet
  "#14b8a6", // teal
  "#84cc16", // lime
  "#d946ef", // fuchsia
  "#334155", // slate-700
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [name, setName] = useState(user?.name ?? "");
  const [colour, setColour] = useState(user?.colour ?? PRESET_COLOURS[0]!);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidHex = /^#[0-9a-fA-F]{6}$/.test(colour);
  const trimmedName = name.trim();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!trimmedName) return setError("Please choose a name your flatmates will see.");
    if (trimmedName.length > 50) return setError("Name is a bit long — keep it under 50 characters.");
    if (!isValidHex) return setError("Pick a valid hex colour like #6366f1.");

    setSubmitting(true);
    try {
      await api.patch<User>("/auth/me", { name: trimmedName, colour });
      await refreshUser();
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        errMsg(err, "Couldn't save your profile. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-gradient-to-br from-brand-50 via-white to-slate-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Set up your profile</h1>
          <p className="mt-1 text-sm text-slate-500">
            Pick a name and a colour your flatmates will recognise.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-col items-center gap-3">
            <Avatar
              name={trimmedName || "?"}
              colour={isValidHex ? colour : "#94a3b8"}
              size="lg"
            />
            <p className="text-xs text-slate-500">Preview</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name">Display name</Label>
              <Input
                id="name"
                autoFocus
                required
                placeholder="e.g. Alex"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                disabled={submitting}
              />
            </div>

            <div>
              <Label>Pick a colour</Label>
              <div className="grid grid-cols-5 gap-2">
                {PRESET_COLOURS.map((c) => {
                  const selected = c.toLowerCase() === colour.toLowerCase();
                  return (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setColour(c)}
                      aria-label={`Choose ${c}`}
                      className={`relative h-10 w-full rounded-lg transition ${
                        selected
                          ? "ring-2 ring-offset-2 ring-brand-500"
                          : "ring-1 ring-slate-200 hover:ring-slate-300"
                      }`}
                      style={{ background: c }}
                    >
                      {selected && (
                        <LuCheck className="absolute inset-0 m-auto h-5 w-5 text-white drop-shadow" />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="color"
                  value={isValidHex ? colour : "#6366f1"}
                  onChange={(e) => setColour(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-slate-200 bg-white"
                />
                <Input
                  value={colour}
                  onChange={(e) => setColour(e.target.value)}
                  placeholder="#6366f1"
                  maxLength={7}
                />
              </div>
            </div>

            <ErrorBanner message={error} />

            <Button type="submit" loading={submitting} className="w-full">
              Continue
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
